import type { FormSelectOption, RowType } from '@tryhackme/thm-ui-components';
import type { ManageRoomDetail, RoomVersion, SortField, SortOrder } from '../../manage-room.types';
import { faClockRotateLeft, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { faCircleSmall } from '@fortawesome/pro-solid-svg-icons';
import { Avatar, CellBorderWidthEnum, IconButton, IconTag, ModularTable, NotificationDispatch, Pagination, SortEnum, TableColorThemeEnum, TextField } from '@tryhackme/thm-ui-components';
import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { useGetRoomVersionsQuery, useRestoreRoomVersionMutation } from '../../manage-room.slice';
import { ModalLayout } from '../modal';
import { StyledEmptyStateContainer, StyledEmptyStateDescription, StyledEmptyStateHeading } from './components/empty-state/empty-state.styles';
import DOMPurify from 'dompurify';
import { VersionControlActionsModal, VersionControlEmptyState } from './components';
import { paginationOptions } from './room-version-tab.constants';
import { ButtonWrapper, StyledAvatarContainer, StyledChangesContainer, StyledChangesText, StyledDefaultText, StyledDraftStatusTag, StyledHeaderContainer, StyledHeading, StyledHtmlContentContainer, StyledModularTableWrapper, StyledOptionsContainer, StyledPaginationContainer, StyledPublicStatusTag, StyledRecordDetailText, StyledRecordsContainer, StyledResetIcon, StyledSearchContainer, StyledSearchIcon, StyledStatusIcon, StyledUsersIcon, StyledViewButton } from './room-version-tab.styles';

// Simple function to extract first 20 characters from HTML
const extractTextFromHtml = (htmlString: string): string => {
  if (!htmlString) return 'No description';

  // Strip HTML tags using regex (safer and more efficient)
  const plainText = htmlString.replace(/<[^>]*>/g, '').trim();
  return plainText.slice(0, 20) || 'No description';
};

// Add a mapping function to convert the sortField to the corresponding column key
const getSortColumnKey = (field: string): string => {
  switch (field) {
    case 'createdAt':
      {
        return 'date';
      }
    case 'savedBy.username':
      {
        return 'author';
      }
    case 'version':
      {
        return 'version';
      }
    default:
      {
        return 'date';
      }
  }
};
export const VersionControlTab = ({
  roomDetails
}: {
  roomDetails?: ManageRoomDetail;
}) => {
  const roomCode = roomDetails?.code;
  const [paginationLimit, setPaginationLimit] = useState(paginationOptions[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState(SortEnum.descending);
  const [sortField, setSortField] = useState('createdAt');
  const [searchText, setSearchText] = useState('');

  // State for restore modal
  const [openResetModal, setOpenResetModal] = useState(false);
  const [selectedVersionCode, setSelectedVersionCode] = useState('');

  // State for changes modal
  const [openChangesModal, setOpenChangesModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<RoomVersion | null>(null);
  const limit = Number(paginationLimit?.value);
  const {
    data,
    isLoading
  } = useGetRoomVersionsQuery({
    roomCode: roomCode ?? '',
    page: currentPage,
    limit,
    searchText,
    sortOrder: sortOrder as SortOrder,
    sortField: sortField as SortField
  }, {
    skip: !roomCode
  });
  const [restoreRoomVersion] = useRestoreRoomVersionMutation();
  const versions = data?.data?.docs ?? [];
  const totalPages = data?.data?.totalPages ?? 0;
  const totalDocs = data?.data?.totalDocs ?? 0;
  const pagingCounter = data?.data?.pagingCounter ?? 0;
  const hasNextPage = data?.data?.hasNextPage;

  // Only show empty state when there's no search text and no versions
  const showEmptyState = Boolean(!versions?.length && !searchText);

  // Check if we're showing no results due to search
  const showNoSearchResults = Boolean(!versions?.length && searchText);
  const rows = versions.map((version: RoomVersion) => {
    let statusElement: React.ReactNode;
    let versionElement: React.ReactNode;
    const lowerStatus = version.status.toLowerCase();
    switch (true) {
      case version.isVersionLive:
        {
          versionElement = <IconTag type="success">
            <StyledStatusIcon icon={faCircleSmall} />
            {`v${version.version} Live `}
          </IconTag>;
          break;
        }
      default:
        {
          versionElement = <StyledDefaultText>{lowerStatus.includes('draft') ? '-' : `v${version.version}`}</StyledDefaultText>;
        }
    }
    switch (true) {
      case lowerStatus === 'live':
        {
          statusElement = <IconTag type="success">
            <StyledStatusIcon icon={faCircleSmall} />
            {version.status}
          </IconTag>;
          break;
        }
      case lowerStatus === 'private':
        {
          statusElement = <IconTag type="warning">{version.status}</IconTag>;
          break;
        }
      case lowerStatus === 'public':
        {
          statusElement = <StyledPublicStatusTag type="info">{version.status}</StyledPublicStatusTag>;
          break;
        }
      case lowerStatus.includes('draft'):
        {
          statusElement = <StyledDraftStatusTag type="info">{version.status}</StyledDraftStatusTag>;
          break;
        }
      default:
        {
          // Default fallback for any other statuses
          statusElement = <IconTag type="info">{version.status}</IconTag>;
          break;
        }
    }
    return {
      id: version._id,
      version: versionElement,
      date: <StyledDefaultText>{new Date(version.createdAt).toLocaleString()}</StyledDefaultText>,
      author: <StyledAvatarContainer>
          <div>
            <Avatar hasBorder={false} avatarSrc={version?.savedBy?.avatar || ''} size="3.2rem" />
          </div>

          <StyledDefaultText>{version?.savedBy?.username || 'Unknown'}</StyledDefaultText>
        </StyledAvatarContainer>,
      changes: <StyledChangesContainer>
          <StyledChangesText>{extractTextFromHtml(version?.changeDescription || '')}</StyledChangesText>
          <ButtonWrapper>
            <StyledViewButton variant="outlined" color="secondary" onClick={() => handleViewChanges(version)}>
              View all
            </StyledViewButton>
          </ButtonWrapper>
        </StyledChangesContainer>,
      status: statusElement,
      reset: <IconButton data-testid="reset-button" variant="edit" height="3.6rem" width="3.6rem" disabled={version.status !== version.liveRoomStatus} onClick={() => handleRestoreVersion(version.versionCode)}>
          <StyledResetIcon icon={faClockRotateLeft} />
        </IconButton>
    };
  }) as RowType[];
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  const handleSearchValue = useDebouncedCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPage(1);
    setSearchText(e.target.value);
  }, 350);
  const handleRestoreVersion = (versionCode: string) => {
    setSelectedVersionCode(versionCode);
    setOpenResetModal(true);
  };
  const handleViewChanges = (version: RoomVersion) => {
    setSelectedVersion(version);
    setOpenChangesModal(true);
  };
  const handlePaginationLimit = (value?: FormSelectOption) => {
    setCurrentPage(1);
    setPaginationLimit(value);
  };
  const handleRestoreVersionApi = async () => {
    if (!roomCode) {
      NotificationDispatch('error', 'Room code is not available');
      return;
    }
    try {
      await restoreRoomVersion({
        versionCode: selectedVersionCode,
        roomCode
      }).unwrap();
      NotificationDispatch('success', 'Room version has been successfully restored.');
    } catch (error) {
      if (isQueryErrorWithMessage(error) && (error.status === 400 || error.status === 403)) {
        NotificationDispatch('error', error.data.message);
      } else {
        NotificationDispatch('error', 'Uh-oh. There was an error while restoring room version. Please try again.');
      }
    } finally {
      setOpenResetModal(false);
    }
  };
  const handleModalRestore = () => {
    handleRestoreVersionApi().catch(() => {
      NotificationDispatch('error', 'An unexpected error occurred. Please try again.');
    });
  };
  return <section aria-label="Room Versions" data-sentry-component="VersionControlTab" data-sentry-source-file="version-control.tsx">
      <StyledHeaderContainer data-sentry-element="StyledHeaderContainer" data-sentry-source-file="version-control.tsx">
        <StyledUsersIcon icon={faClockRotateLeft} data-sentry-element="StyledUsersIcon" data-sentry-source-file="version-control.tsx" />
        <StyledHeading data-sentry-element="StyledHeading" data-sentry-source-file="version-control.tsx">Room Versions</StyledHeading>
      </StyledHeaderContainer>

      <StyledOptionsContainer data-sentry-element="StyledOptionsContainer" data-sentry-source-file="version-control.tsx">
        <StyledSearchContainer data-sentry-element="StyledSearchContainer" data-sentry-source-file="version-control.tsx">
          <TextField variant="input" placeholder="Search versions" leftAdornment={<StyledSearchIcon icon={faMagnifyingGlass} />} onChange={handleSearchValue} data-sentry-element="TextField" data-sentry-source-file="version-control.tsx" />
        </StyledSearchContainer>

        <StyledRecordsContainer data-sentry-element="StyledRecordsContainer" data-sentry-source-file="version-control.tsx">
          <TextField isDisabled={showEmptyState} isMulti={false} variant="select" selected={paginationLimit} options={paginationOptions} isSearchable={false} handleSelection={value => handlePaginationLimit(value)} leftOutsideAdornment={<StyledDefaultText>show</StyledDefaultText>} rightOutsideAdornment={<StyledDefaultText>records</StyledDefaultText>} data-sentry-element="TextField" data-sentry-source-file="version-control.tsx" />
        </StyledRecordsContainer>
      </StyledOptionsContainer>

      <StyledModularTableWrapper data-sentry-element="StyledModularTableWrapper" data-sentry-source-file="version-control.tsx">
        <ModularTable isLoading={isLoading} activeSortColumn={showEmptyState ? null : getSortColumnKey(sortField)} activeSortOrder={sortOrder} cellBorderWidth={CellBorderWidthEnum.thick} colorTheme={TableColorThemeEnum.grayWhite} columns={[{
        flex: '1.2',
        key: 'version',
        title: 'Version'
      }, {
        flex: '2',
        key: 'date',
        sortFunction: () => {
          setSortField('createdAt');
          setSortOrder(prev => prev === SortEnum.ascending ? SortEnum.descending : SortEnum.ascending);
        },
        title: 'Date & time'
      }, {
        flex: '2',
        key: 'author',
        sortFunction: () => {
          setSortField('savedBy.username');
          setSortOrder(prev => prev === SortEnum.ascending ? SortEnum.descending : SortEnum.ascending);
        },
        title: 'Author'
      }, {
        flex: '3',
        key: 'changes',
        title: 'Changes'
      }, {
        flex: '1',
        key: 'status',
        title: 'Status'
      }, {
        flex: '1',
        key: 'reset',
        justifyContent: 'center',
        title: 'Restore &  Publish'
      }]} rows={rows} data-sentry-element="ModularTable" data-sentry-source-file="version-control.tsx" />
        {!isLoading && showEmptyState && <VersionControlEmptyState roomCode={roomCode} />}
        {!isLoading && showNoSearchResults && <StyledEmptyStateContainer>
            <StyledEmptyStateHeading>No versions found</StyledEmptyStateHeading>
            <StyledEmptyStateDescription>Try different search terms</StyledEmptyStateDescription>
          </StyledEmptyStateContainer>}
      </StyledModularTableWrapper>

      {!isLoading && !showEmptyState && !showNoSearchResults && <StyledPaginationContainer>
          <StyledRecordDetailText>{`Showing ${pagingCounter} to ${hasNextPage ? pagingCounter + (limit - 1) : totalDocs} of ${totalDocs} records`}</StyledRecordDetailText>

          <div>
            <Pagination totalPages={totalPages} selectedPage={currentPage} onChange={handlePageChange} />
          </div>
        </StyledPaginationContainer>}

      <VersionControlActionsModal open={openResetModal} openChange={setOpenResetModal} titleText="Reset changes" message="Resetting changes will delete your current draft. Are you sure you want to proceed?" actionButtonText="Reset changes" handleModalAction={handleModalRestore} data-sentry-element="VersionControlActionsModal" data-sentry-source-file="version-control.tsx" />

      <ModalLayout open={openChangesModal} openChange={setOpenChangesModal} showTriggerElement={false} modalTitleText={`Version ${selectedVersion?.version || ''} Changes`} showHeaderIcon={false} closeButtonText="Close" actionElement={false} showWarnBox={false} data-sentry-element="ModalLayout" data-sentry-source-file="version-control.tsx">
        <StyledHtmlContentContainer dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(selectedVersion?.changeDescription || '') || 'No description available for this version.'
      }} data-sentry-element="StyledHtmlContentContainer" data-sentry-source-file="version-control.tsx" />
      </ModalLayout>
    </section>;
};